import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Code2, MapPin, Github, Twitter, Instagram } from 'lucide-react';

const Logo = styled.div`
  font-family: 'Outfit', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  
  span {
    background: linear-gradient(to right, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const RouterNavLink = styled(Link)`
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
  transition: color 0.3s;
  
  &:hover {
    color: var(--text-main);
  }
`;

const FooterContainer = styled.footer`
  padding: 80px 5% 40px;
  background: #010409;
  border-top: 1px solid var(--glass-border);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
    justify-items: center;
    
    .social-links {
      justify-content: center;
    }

    > div {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
`;

const Footer = () => {
    const { t } = useTranslation();
    const [technologies, setTechnologies] = useState([]);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL || ''}/api/technologies`)
            .then(res => res.json())
            .then(data => setTechnologies(Array.isArray(data) ? data : []))
            .catch(console.error);
    }, []);

    return (
        <FooterContainer>
            <FooterContent>
                <div style={{ gridColumn: 'span 1' }}>
                    <Logo style={{ marginBottom: '24px', justifyContent: 'center' }}>
                        <Code2 size={24} color="var(--primary)" />
                        <span>Nexova Dev LLC</span>
                    </Logo>
                    <p style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{t('footer.tagline')}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <MapPin size={16} /> {t('contact.address')}
                    </p>
                    <div className="social-links" style={{ display: 'flex', gap: '20px', marginTop: '30px' }}>
                        <a href="https://github.com/Nexova-Dev" target="_blank" rel="noreferrer" style={{ color: 'var(--text-muted)' }}><Github size={20} /></a>
                        <a href="https://x.com/nexova_dev" target="_blank" rel="noreferrer" style={{ color: 'var(--text-muted)' }}><Twitter size={20} /></a>
                        <a href="https://www.instagram.com/nexova_dev/" target="_blank" rel="noreferrer" style={{ color: 'var(--text-muted)' }}><Instagram size={20} /></a>
                    </div>
                </div>
                <div>
                    <h4 style={{ marginBottom: '24px' }}>{t('footer.nav')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <RouterNavLink to="/about">{t('nav.about')}</RouterNavLink>
                        <RouterNavLink to="/services">{t('nav.services')}</RouterNavLink>
                        <RouterNavLink to="/projects">{t('nav.projects')}</RouterNavLink>
                        <RouterNavLink to="/contact">{t('nav.contact')}</RouterNavLink>
                    </div>
                </div>
                <div>
                    <h4 style={{ marginBottom: '24px' }}>{t('footer.tech')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {technologies.slice(0, 5).map(tech => (
                            tech.link ? (
                                <RouterNavLink key={tech.id} to={tech.link}>
                                    {tech.name}
                                </RouterNavLink>
                            ) : (
                                <span key={tech.id} style={{ color: 'var(--text-muted)' }}>
                                    {tech.name}
                                </span>
                            )
                        ))}
                        {technologies.length === 0 && (
                            <>
                                <RouterNavLink to="/tech/react">React / Next.js</RouterNavLink>
                                <RouterNavLink to="/tech/node">Node.js</RouterNavLink>
                                <RouterNavLink to="/tech/ai">AI / ML</RouterNavLink>
                                <RouterNavLink to="/tech/cloud">Cloud Infrastructure</RouterNavLink>
                            </>
                        )}
                    </div>
                </div>
                <div>
                    <h4 style={{ marginBottom: '24px' }}>{t('footer.support')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <RouterNavLink to="/faq">FAQ</RouterNavLink>
                        <RouterNavLink to="/contact">{t('nav.contact')}</RouterNavLink>
                        <RouterNavLink to="/privacy">{t('footer.privacy')}</RouterNavLink>
                    </div>
                </div>
            </FooterContent>
            <div style={{
                textAlign: 'center',
                marginTop: '80px',
                paddingTop: '40px',
                borderTop: '1px solid var(--glass-border)',
                color: 'var(--text-muted)',
                fontSize: '0.85rem'
            }}>
                &copy; {new Date().getFullYear()} Nexova Dev LLC. All rights reserved.
            </div>
        </FooterContainer>
    );
};

export default Footer;
